/**
 * AdminFailedPayouts — one-stop admin queue for every payout_records row
 * that ended in `status='failed'`.
 *
 * Motivation (P0.16 follow-up, Anita Currie / Dallas incident): the fix
 * to `admin_trigger_payout` now returns structured 400s (with
 * `balance_insufficient` + hint) instead of Cloudflare-splash 502s. But
 * admins still had no roll-up view of every failed transfer across every
 * shift — this page delivers exactly that, plus one-click and bulk retry
 * that route through the same fixed backend path.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, RefreshCw, Loader2, AlertTriangle, PlayCircle, Users,
  Zap, Wallet, DollarSign, Mail, MapPin,
} from 'lucide-react';
import PlatformBalanceBanner from '../../components/PlatformBalanceBanner';

const fmt = (n) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2,
}).format(n || 0);

const shortDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

const AdminFailedPayouts = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState({});      // { payout_id: true }
  const [bulkRetryBusy, setBulkRetryBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/payouts/failed?limit=200');
      setRows(data.rows || []);
      setCount(data.count || 0);
      setTotalAmount(data.total_amount_usd || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load failed payouts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const retryOne = async (row) => {
    setRetrying((prev) => ({ ...prev, [row.payout_id]: true }));
    try {
      const res = await api.post(`/admin/payouts/${row.payout_id}/retry`);
      const amt = res.data?.amount ?? row.amount;
      toast.success(`Retry sent — ${fmt(amt)} → ${row.talent_label}`);
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      const httpTxt = e.response?.status ? ` (HTTP ${e.response.status})` : '';
      const msg = typeof d === 'string'
        ? d
        : (d && (d.message || d.hint || d.code)) || e.message || 'Retry failed';
      toast.error(`${msg}${httpTxt}`);
    } finally {
      setRetrying((prev) => {
        const next = { ...prev };
        delete next[row.payout_id];
        return next;
      });
    }
  };

  const retryBulk = async () => {
    if (rows.length === 0) return;
    if (!window.confirm(
      `Retry ${rows.length} failed payout${rows.length === 1 ? '' : 's'} ` +
      `(${fmt(totalAmount)} total)? Stops automatically on the first ` +
      `insufficient-funds error to avoid burning Stripe API calls.`
    )) return;
    setBulkRetryBusy(true);
    try {
      const { data } = await api.post('/admin/payouts/retry-bulk', {});
      const parts = [
        `${data.succeeded} succeeded`,
        `${data.failed} failed`,
      ];
      if (data.stopped_early) parts.push('stopped at balance_insufficient');
      toast.success(`Bulk retry: ${parts.join(' · ')}`);
      if (data.failures?.length) {
        const firstFail = data.failures[0];
        const dMsg = typeof firstFail.detail === 'string'
          ? firstFail.detail
          : (firstFail.detail?.message || firstFail.code || 'unknown');
        toast.error(`First failure: ${dMsg}`);
      }
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.message || 'Bulk retry failed');
      toast.error(msg);
    } finally {
      setBulkRetryBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="admin-failed-payouts-page">
      <div className="border-b bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} data-testid="failed-payouts-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" /> Failed Payouts
            </h1>
            <p className="text-xs text-slate-500">
              Every payout attempt that ended in <code>status=failed</code> and hasn&apos;t been superseded by a newer attempt.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="failed-payouts-refresh"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        <PlatformBalanceBanner data-testid="failed-payouts-balance-banner" />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card data-testid="failed-count-card">
            <CardContent className="p-4">
              <p className="text-[11px] uppercase tracking-wide text-slate-500">Failed payouts</p>
              <p className="text-2xl font-semibold" data-testid="failed-count-value">{count}</p>
            </CardContent>
          </Card>
          <Card data-testid="failed-total-card">
            <CardContent className="p-4">
              <p className="text-[11px] uppercase tracking-wide text-slate-500">Total unpaid</p>
              <p className="text-2xl font-semibold text-amber-700" data-testid="failed-total-value">{fmt(totalAmount)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex flex-col justify-center h-full">
              <Button
                onClick={retryBulk}
                disabled={bulkRetryBusy || rows.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700 text-white w-full"
                data-testid="failed-retry-all-btn"
              >
                {bulkRetryBusy
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <Zap className="w-4 h-4 mr-2" />}
                Retry all ({rows.length})
              </Button>
              <p className="text-[11px] text-slate-500 mt-1">
                Stops on first balance_insufficient
              </p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-500" /> Talent awaiting retry
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading && rows.length === 0 ? (
              <div className="p-10 flex justify-center text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : rows.length === 0 ? (
              <div className="p-10 text-center">
                <p className="text-slate-500 text-sm">
                  🎉 No failed payouts. Everything owed has either paid or is in flight.
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {rows.map((r) => {
                  const isRetrying = !!retrying[r.payout_id];
                  const reasonCode =
                    /insufficient/i.test(r.failed_reason || '')      ? 'balance_insufficient'
                    : /account.*inactive|permission/i.test(r.failed_reason || '') ? 'stripe_permission'
                    : /invalid/i.test(r.failed_reason || '')          ? 'invalid_stripe_account'
                    : 'stripe_error';
                  return (
                    <div
                      key={r.payout_id}
                      className="px-4 py-3 flex flex-wrap items-start gap-3"
                      data-testid={`failed-row-${r.payout_id}`}
                    >
                      <div className="flex-1 min-w-[240px]">
                        <div className="font-medium flex items-center gap-1.5">
                          {r.talent_label}
                          {!r.onboarded && (
                            <Badge className="bg-red-50 text-red-700 border border-red-200 text-[10px]">
                              Stripe not onboarded
                            </Badge>
                          )}
                          {r.attempts > 1 && (
                            <Badge
                              className="bg-slate-100 text-slate-700 border border-slate-200 text-[10px]"
                              data-testid={`failed-attempts-${r.payout_id}`}
                              title="Number of failed retries for this booking"
                            >
                              {r.attempts} attempts
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                          {r.talent_email && (
                            <span className="flex items-center gap-0.5">
                              <Mail className="w-3 h-3" /> {r.talent_email}
                            </span>
                          )}
                          {(r.event_title || r.shift_title) && (
                            <span className="flex items-center gap-0.5">
                              <MapPin className="w-3 h-3" /> {r.event_title || r.shift_title}
                            </span>
                          )}
                          {r.shift_date && (
                            <span>{r.shift_date}</span>
                          )}
                        </div>
                        <div className="text-xs text-red-700 mt-1 truncate max-w-full" title={r.failed_reason}>
                          <span className="font-mono bg-red-50 border border-red-100 rounded px-1 mr-1">
                            {reasonCode}
                          </span>
                          {r.failed_reason || 'Unknown error'}
                        </div>
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          Last attempt: {shortDate(r.last_attempt_at)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-semibold text-slate-800 flex items-center gap-1 justify-end">
                          <DollarSign className="w-4 h-4 text-slate-400" />
                          {fmt(r.amount).replace('$', '')}
                        </div>
                        {r.reimbursement > 0 && (
                          <div className="text-[11px] text-slate-500">
                            incl. {fmt(r.reimbursement)} reimb
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/admin/shifts/${r.shift_id}`)}
                          disabled={!r.shift_id}
                          data-testid={`failed-view-shift-btn-${r.payout_id}`}
                        >
                          View shift
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => retryOne(r)}
                          disabled={isRetrying}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`failed-retry-btn-${r.payout_id}`}
                        >
                          {isRetrying
                            ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                            : <PlayCircle className="w-3.5 h-3.5 mr-1" />}
                          Retry
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminFailedPayouts;
